import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useHistory } from "react-router-dom";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableCheckboxCell,
    DataTableColumnHeaderCell,
    DataTableColumnManageButton,
    DataTableContent,
    DataTableHeaderCheckboxCell,
    DataTableHeaderRow,
    DataTableOverflowMenuCell,
    DataTablePagination,
    DataTableRow,
    DataTableToolbar,
    Dialog,
    List,
    ListItem,
    RadioButton,
    RadioButtonGroup,
    Search,
    SortDirection,
    TextButton,
    Typography,
} from "@episerver/ui-framework";
import {
    deselectAllOrganizations,
    deselectOrganization,
    initialOrganizationState,
    onChangeConfirmDialog,
    selectAllOrganizations,
    selectOrganization,
    sort,
    reset,
} from "../reducers/organizationListReducer";
import { searchOrganizations, deleteOrganizations } from "../reducers/asyncThunks";
import { DeleteMode, Organization, ServerSortType } from "../model/organization";
import { debounce } from "lodash-es";
import { RootState } from "../../store";

const OrganizationList = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const {
        keyword,
        organizations,
        selectedOrganizations,
        sort: { sortDirection, sortedColumn },
        pagination: { rowsPerPage, startRow, rowsPerPageOptions, totalRowCount },
        confirmDialog,
    } = useAppSelector((state: RootState) => state.organizationList);
    const sortDirectionRef = useRef<SortDirection>(sortDirection);
    const sortedColumnRef = useRef<string>(sortedColumn);

    useEffect(() => {
        sortDirectionRef.current = sortDirection;
    }, [sortDirection]);

    useEffect(() => {
        sortedColumnRef.current = sortedColumn;
    }, [sortedColumn]);

    useEffect(() => {
        dispatch(
            searchOrganizations({
                query: "",
                sortField: sortedColumn,
                sortType: sortDirection === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: startRow,
                recordsToRetrieve: rowsPerPage,
            })
        );

        return () => {
            dispatch(reset());
        };
    }, []);

    const [columns, setColumns] = useState<
        Record<string, { selector: string; name: string; sortable: boolean; visible: boolean }>
    >({
        name: { selector: "Name", name: "Name", sortable: true, visible: true },
        description: { selector: "Description", name: "Description", sortable: true, visible: true },
        organizationType: { selector: "OrganizationType", name: "OrganizationType", sortable: true, visible: true }
    });

    const onChangeDeleteMode = (deleteMode: DeleteMode) => {
        dispatch(
            onChangeConfirmDialog({
                ...confirmDialog,
                data: { ...confirmDialog.data, mode: deleteMode },
            })
        );
    };

    const confirm = (organizationIds: string[]): void => {
        dispatch(
            onChangeConfirmDialog({
                open: true,
                data: {
                    mode: DeleteMode.Detach,
                    organizationIds: organizationIds,
                },
            })
        );
    };

    const onConfirm = (mode: DeleteMode) => {
        dispatch(
            deleteOrganizations({
                organizationIds: confirmDialog.data.organizationIds,
                mode: mode,
                query: keyword,
                sortField: sortedColumn,
                sortType: sortDirection === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: startRow,
                recordsToRetrieve: rowsPerPage,
            })
        );
        dispatch(onChangeConfirmDialog(initialOrganizationState.confirmDialog));
    };

    const onCancel = () => {
        dispatch(onChangeConfirmDialog(initialOrganizationState.confirmDialog));
    };

    const onDeleteSelected = (): void => {
        confirm(selectedOrganizations);
    };

    const onDelete = (organizationId: string): void => {
        confirm([organizationId]);
    };

    const onSelectedRowChanged = (row: Organization, checked: boolean): void => {
        if (checked) dispatch(selectOrganization(row.primaryKeyId));
        else dispatch(deselectOrganization(row.primaryKeyId));
    };

    const onToggleAllSelected = (checked: boolean): void => {
        if (checked) dispatch(selectAllOrganizations());
        else dispatch(deselectAllOrganizations());
    };

    const isSelected = (row: Organization): boolean => {
        return selectedOrganizations.includes(row.primaryKeyId!);
    };

    const handleSearch = (value: string, recordsToRetrieve: number) => debouncedSearch(value, recordsToRetrieve);
    const debouncedSearch = useCallback(
        debounce((nextValue: string, recordsToRetrieve: number) => onSearch(nextValue, recordsToRetrieve), 1000),
        []
    );
    const onSearch = (value: string, recordsToRetrieve: number): void => {
        let keyWord = value.trim();
        dispatch(
            searchOrganizations({
                query: keyWord,
                sortField: sortedColumnRef.current,
                sortType:
                    sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: 0,
                recordsToRetrieve: recordsToRetrieve,
            })
        );
    };

    const onSort = (column: string, direction: SortDirection): void => {
        dispatch(sort({ column, direction }));
        dispatch(
            searchOrganizations({
                query: keyword,
                sortField: column,
                sortType: direction === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: startRow,
                recordsToRetrieve: rowsPerPage,
            })
        );
    };

    const onChangePage = (startRow: number): void => {
        dispatch(
            searchOrganizations({
                query: keyword,
                sortField: sortedColumnRef.current,
                sortType:
                    sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: startRow,
                recordsToRetrieve: rowsPerPage,
            })
        );
    };

    const onChangeRowsPerPage = (rowsPerPage: number): void => {
        dispatch(
            searchOrganizations({
                query: keyword,
                sortField: sortedColumnRef.current,
                sortType:
                    sortDirectionRef.current === SortDirection.Ascending ? ServerSortType.Asc : ServerSortType.Desc,
                startIndex: 0,
                recordsToRetrieve: rowsPerPage,
            })
        );
    };

    const renderRow = (rowData: Organization): React.ReactElement => {
        return (
            <DataTableRow rowId={rowData.primaryKeyId!} key={rowData.primaryKeyId} selected={isSelected(rowData)}>
                <DataTableCheckboxCell
                    defaultChecked={isSelected(rowData)}
                    onChange={(e) => onSelectedRowChanged(rowData, e.target.checked)}
                />
                <>
                    {columns["name"].visible && (
                        <DataTableCell>
                            <a
                                href=""
                                className="underline-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    history.push("/Organizations/Edit/" + rowData.primaryKeyId);
                                }}
                            >
                                {rowData.name}
                            </a>
                        </DataTableCell>
                    )}
                </>
                <>
                    {columns["description"].visible && (
                        <DataTableCell>{rowData.description ?? ""}</DataTableCell>
                    )}
                </>
                <>
                    {columns["organizationType"].visible && (
                        <DataTableCell>{rowData.organizationType ?? ""}</DataTableCell>
                    )}
                </>
                <DataTableOverflowMenuCell>
                    <List>
                        <ListItem onClick={() => history.push("/Organizations/Edit/" + rowData.primaryKeyId)}>
                            Edit
                        </ListItem>
                        <ListItem onClick={() => onDelete(rowData.primaryKeyId!)}>Delete</ListItem>
                    </List>
                </DataTableOverflowMenuCell>
            </DataTableRow>
        );
    };

    return (
        <div className="organization-list">
            <Typography use="headline1">Organizations</Typography>
            <DataTable>
                <DataTableToolbar className="organization-list__toolbar">
                    <React.Fragment>
                        <div>
                            <TextButton onClick={() => history.push("/Organizations/Edit")}>
                                Create
                            </TextButton>
                            {selectedOrganizations.length > 0 && (
                                <TextButton onClick={() => onDeleteSelected()}>
                                    Delete Selected ({selectedOrganizations.length})
                                </TextButton>
                            )}
                        </div>
                        <Search
                            value={keyword}
                            onValueChange={(value: string) => {
                                handleSearch(value, rowsPerPage);
                            }}
                            onSearch={(value: string) => {
                                handleSearch(value, rowsPerPage);
                            }}
                        />
                    </React.Fragment>
                </DataTableToolbar>
                <DataTableContent>
                    <DataTableHeaderRow>
                        <DataTableHeaderCheckboxCell
                            className={"w60"}
                            onClick={(e: any) => onToggleAllSelected(e.target.checked)}
                        />
                        <React.Fragment>
                            {Object.keys(columns).map((column) => {
                                if (columns[column].sortable)
                                    return (
                                        columns[column].visible && (
                                            <DataTableColumnHeaderCell
                                                key={columns[column].selector}
                                                onSort={(_header: string, direction: SortDirection) =>
                                                    onSort(columns[column].selector, direction)
                                                }
                                                sortDirection={
                                                    sortedColumn === columns[column].selector
                                                        ? sortDirection
                                                        : SortDirection.Unsorted
                                                }
                                            >
                                                {columns[column].name}
                                            </DataTableColumnHeaderCell>
                                        )
                                    );
                                return (
                                    columns[column].visible && (
                                        <DataTableColumnHeaderCell key={columns[column].selector}>
                                            {columns[column].name}
                                        </DataTableColumnHeaderCell>
                                    )
                                );
                            })}
                        </React.Fragment>
                        <DataTableColumnManageButton
                            className={"w57"}
                            columns={Object.values(columns)}
                            onSelectChange={(columnName, checked) => {
                                const changedColumn = Object.keys(columns).find(
                                    (column) => columns[column].name === columnName
                                );
                                if (changedColumn) {
                                    setColumns({
                                        ...columns,
                                        [changedColumn]: { ...columns[changedColumn], visible: checked },
                                    });
                                }
                            }}
                        />
                    </DataTableHeaderRow>
                    <DataTableBody>
                        <>
                            {organizations.length == 0 && (
                                <DataTableRow rowId={"0"}>
                                    <DataTableCell>
                                        <Typography use="body1">No Organizations found.</Typography>
                                    </DataTableCell>
                                </DataTableRow>
                            )}
                            {organizations.map(renderRow)}
                        </>
                    </DataTableBody>
                </DataTableContent>
                <DataTablePagination
                    startRow={startRow}
                    totalRowCount={totalRowCount}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onChangePage={onChangePage}
                    onChangeRowsPerPage={onChangeRowsPerPage}
                />
            </DataTable>
            <Dialog
                open={confirmDialog.open}
                title="Delete"
                dismissLabel="Cancel"
                confirmLabel="Delete"
                enableConfirm
                onInteraction={(e) => {
                    if (e.detail.action === "confirm") onConfirm(confirmDialog.data.mode);
                    else onCancel();
                }}
            >
                <React.Fragment>
                    <p>
                        Selected organizations can have related contacts and child organizations. What do you want to
                        delete?
                    </p>
                    <RadioButtonGroup header="" name="mode">
                        <RadioButton
                            value="1"
                            checked={confirmDialog.data.mode === DeleteMode.Detach}
                            onChange={() => onChangeDeleteMode(DeleteMode.Detach)}
                        >
                            Delete Organizations Only
                        </RadioButton>
                        <RadioButton
                            value="2"
                            checked={confirmDialog.data.mode === DeleteMode.Delete}
                            onChange={() => onChangeDeleteMode(DeleteMode.Delete)}
                        >
                            Delete Organizations, Child Organizations and Contacts.
                        </RadioButton>
                    </RadioButtonGroup>
                </React.Fragment>
            </Dialog>
        </div>
    );
};

export default OrganizationList;
